/**
 * useFeedWS — subscribe to topic 'feed' and invalidate the feed query.
 *
 * Server (Fase 5 feed.subscribers) broadcasts verb-only payloads on the /ws
 * hub's 'feed' topic:
 *   { topic:'feed', event:'item_added' | 'reaction_changed', data:{ id } }
 *
 * Both verbs invalidate ['feed'] so the cache refetches the authoritative
 * page — WS events never patch row data directly (T-02-31 mitigation, mirrors
 * useChoresWS / usePointsWS). Mounted app-wide in App's RealtimeSync so the
 * feed stays live wherever the user is; invalidateQueries only refetches the
 * query when it is actually mounted, so a global subscription stays cheap.
 *
 * Gjenoppkobling og backoff: se lib/reconnectingSocket.ts.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { openReconnectingSocket } from '../../lib/reconnectingSocket';

export function useFeedWS(): void {
  const qc = useQueryClient();

  useEffect(() => {
    const onMessage = (ev: unknown) => {
      try {
        const data = (ev as { data?: unknown }).data;
        const msg = JSON.parse(String(data)) as { topic?: string; event?: string };
        if (msg?.topic !== 'feed') return;
        switch (msg.event) {
          case 'item_added':
          case 'reaction_changed':
            void qc.invalidateQueries({ queryKey: ['feed'] });
            break;
          default:
            // Unknown verb — ignore (forward compatibility).
            break;
        }
      } catch {
        // Malformed frame — drop silently.
      }
    };

    return openReconnectingSocket({ topics: ['feed'], onMessage });
  }, [qc]);
}
